import { useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { ReactComponent as CheckCircleSVG } from 'icons/table/check-circle.svg';
import { ReactComponent as ClockSVG } from 'icons/table/clock.svg';
import { polygraphyShape, teleconsultationShape } from 'helpers/shared_types';
import useWindowDimensions, {
  isMobileResolution,
} from 'helpers/useWindowDimensions';

import Toolbar from './toolbar';
import ToolbarMobile from './toolbar_mobile';

import './calendar.scss';

const locales = {
  fr: require('date-fns/locale/fr'),
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const EventCalendar = ({
  date,
  view,
  events,
  parentEntity,
  onSelectEvent,
  onSelectSlot,
  onUpdate,
  onTextUpdate,
  onNavigate,
  onView,
  onShowMore,
  loading,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = isMobileResolution(width);
  const dayHeaderFormatString = isMobile ? 'eee MMM dd' : 'eeee MMM dd';

  const { defaultDate, formats, views } = useMemo(
    () => ({
      defaultDate: new Date(),
      formats: {
        dayHeaderFormat: (date, culture, localizer) =>
          localizer.format(date, dayHeaderFormatString, culture),
      },
      views: {
        week: !isMobile,
        month: !isMobile,
        day: true,
      },
    }),
    [isMobile, dayHeaderFormatString],
  );

  const calendarClasses = classnames('br4 shadow-1 bg-grouped-table mt2', {
    filtering: loading,
  });

  const defaultView = 'day';
  const components = {
    toolbar: (props) =>
      isMobile ? <ToolbarMobile {...props} /> : <Toolbar {...props} />,
  };
  const calendarView = isMobile ? 'day' : view;

  return (
    <div className={calendarClasses}>
      <Calendar
        selectable
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        formats={formats}
        defaultDate={defaultDate}
        date={date}
        eventPropGetter={(event) => {
          return {
            style: {
              backgroundColor: event.backgroundColor,
            },
          };
        }}
        titleAccessor={(event) => (
          <div className="flex flex-column">
            <span className={event.cancelled ? 'strike-through' : ''}>
              {event.title}
            </span>

            {event.paymentBadge &&
              view === 'day' &&
              getBadge(event.paymentBadge.status, event.paymentBadge.amount)}
          </div>
        )}
        style={{ height: 1000 }}
        components={components}
        views={views}
        defaultView={defaultView}
        view={calendarView}
        dayLayoutAlgorithm="no-overlap"
        min={parse('8:00am', 'h:mma', new Date())}
        max={parse('11:00pm', 'h:mma', new Date())}
        scrollToTime={new Date()}
        parentEntity={parentEntity}
        onSelectEvent={onSelectEvent}
        onSelectSlot={onSelectSlot}
        onUpdate={onUpdate}
        onTextUpdate={onTextUpdate}
        onNavigate={onNavigate}
        onView={onView}
        onShowMore={onShowMore}
      />
    </div>
  );
};

function getBadge(status, amount) {
  const classes = status === 'success' ? 'th-badge-green' : 'th-badge-grey';
  const icon = status === 'success' ? <CheckCircleSVG /> : <ClockSVG />;

  return (
    <div
      className={`th-badge mt2 ${classes} flex justify-center`}
      style={{ maxWidth: 'fit-content' }}
    >
      {icon}
      {amount}
    </div>
  );
}

EventCalendar.propTypes = {
  date: PropTypes.string,
  view: PropTypes.string,
  parentEntity: PropTypes.string,
  events: PropTypes.arrayOf(
    PropTypes.oneOfType([teleconsultationShape, polygraphyShape]),
  ),
  onSelectEvent: PropTypes.func,
  onSelectSlot: PropTypes.func,
  onUpdate: PropTypes.func,
  onTextUpdate: PropTypes.func,
  onNavigate: PropTypes.func,
  onView: PropTypes.func,
  onShowMore: PropTypes.func,
  loading: PropTypes.bool,
};

export default EventCalendar;
